import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";
import { app } from "../../firebase";
import { ref, uploadBytesResumable, getStorage } from "firebase/storage";
import imageCompression from "browser-image-compression";
import { toast } from "react-toastify";
import Spinner from "../../components/Spinner";
import ButtonPrimary from "../../components/style-components/ButtonPrimary";
import ProductCard from "../../components/ProductCard";
import { FaTimes } from "react-icons/fa";
import {
  addProduct,
  editProduct,
  getProductByIdAdmin,
  renewProduct,
  reset,
} from "../../features/products/productSlice";
import VariantForm from "../../components/admin/VariantForm";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function AdminAddProduct() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const storage = getStorage(app);

  const { productId } = useParams();
  const { product, isLoading, isError, message, isSuccess } = useSelector(
    (state) => state.product
  );

  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (isSuccess && productId) {
      // Reset image editing flags after successful save
      setImageState(prev => ({
        ...prev,
        photoEditted: false,
        galleryEditted: false,
      }));
      
      // Reset the loading state
      setLoading(false);
      
      // Reset success state to prevent multiple triggers
      dispatch(reset());
    }
  }, [isSuccess, productId, dispatch]);

  // Consolidated form state
  const [formData, setFormData] = useState({
    title: "",
    title_english: "",
    latin_title: "",
    category: "",
    category_english: "",
    short_description: "",
    short_description_english: "",
    description: "",
    description_english: "",
    grow_time_min: null,
    grow_time_max: null,
    grow_temp_min: null,
    grow_temp_max: null,
    valid_for: null,
    variant_type: "",
    variant_type_english: "",
    sort_order: 99,
  });

  // Image handling state
  const [imageState, setImageState] = useState({
    image: {},
    gallery: [],
    newImageFiles: [],
    imageFile: null,
    photoEditted: false,
    galleryEditted: false,
  });

  // Variant handling state
  const [variantState, setVariantState] = useState({
    variants: [],
    addNewVariant: false,
    newVariant: {
      variant_name: "",
      variant_name_english: "",
      price: null,
      discount_price: null,
      countInStock: null,
      weight: null,
      height: null,
      width: null,
      length: null,
      sku: ""
    }
  });

  const [loading, setLoading] = useState(false);

  const singleFileInputRef = useRef(null);
  const multipleFileInputRef = useRef(null);

  // Initialize form data
  const initializeFormData = () => {
    setFormData({
      title: "",
      title_english: "",
      latin_title: "",
      category: "",
      category_english: "",
      short_description: "",
      short_description_english: "",
      description: "",
      description_english: "",
      grow_time_min: null,
      grow_time_max: null,
      grow_temp_min: null,
      grow_temp_max: null,
      valid_for: null,
      variant_type: "",
      variant_type_english: "",
      sort_order: 99,
    });
    setImageState({
      image: {},
      gallery: [],
      newImageFiles: [],
      imageFile: null,
      photoEditted: false,
      galleryEditted: false,
    });
    setVariantState({
      variants: [],
      addNewVariant: false,
      newVariant: {
        variant_name: "",
        variant_name_english: "",
        price: null,
        discount_price: null,
        countInStock: null,
        weight: null,
        height: null,
        width: null,
        length: null,
        sku: ""
      }
    });
  };

  // Convert variant pricing for display
  const updateVariantPricing = (variants) => {
    return variants.map((variant) => ({
      ...variant,
      discount_price: (variant.discount_price / 100).toFixed(2),
      price: (variant.price / 100).toFixed(2),
    }));
  };

  useEffect(() => {
    if (isError) {
      toast.error(message);
    }

    if (productId) {
      dispatch(getProductByIdAdmin(productId));
    } else {
      initializeFormData();
    }

    return () => {
      dispatch(reset());
    };
  }, [productId, dispatch, isError, message]);

  useEffect(() => {
    if (product && productId) {
      setFormData({
        title: product.title || "",
        title_english: product.title_english || "",
        latin_title: product.latin_title || "",
        category: product.category || "",
        category_english: product.category_english || "",
        short_description: product.short_description || "",
        short_description_english: product.short_description_english || "",
        description: product.description || "",
        description_english: product.description_english || "",
        grow_time_min: product.grow_time_min,
        grow_time_max: product.grow_time_max,
        grow_temp_min: product.grow_temp_min,
        grow_temp_max: product.grow_temp_max,
        valid_for: product.valid_for,
        variant_type: product.variant_type || "",
        variant_type_english: product.variant_type_english || "",
        sort_order: product.sort_order || 99,
      });

      setImageState(prev => ({
        ...prev,
        image: product.image || {},
        gallery: product.images || [],
      }));

      setVariantState(prev => ({
        ...prev,
        variants: updateVariantPricing(product.variants || []),
      }));
    }
  }, [product, productId]);

  // Compute product object for preview
  const productObject = {
    ...formData,
    image: imageState.image,
    images: imageState.gallery,
    variants: variantState.variants,
  };

  // Form field update handler
  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Image handling
  const handleSingleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setImageState(prev => ({
        ...prev,
        imageFile: file,
        photoEditted: true,
        image: { src: url, name: file.name }
      }));
    }
  };

  const handleMultipleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newGallery = [...imageState.gallery];
    
    files.forEach(file => {
      const url = URL.createObjectURL(file);
      newGallery.push({ src: url, name: file.name, isNew: true });
    });

    setImageState(prev => ({
      ...prev,
      newImageFiles: [...prev.newImageFiles, ...files],
      gallery: newGallery,
      galleryEditted: true,
    }));
  };

  const handleImageDelete = (index) => {
    const newGallery = imageState.gallery.filter((_, i) => i !== index);
    const newNewImageFiles = imageState.newImageFiles.filter((_, i) => i !== index);
    
    setImageState(prev => ({
      ...prev,
      gallery: newGallery,
      newImageFiles: newNewImageFiles,
      galleryEditted: true,
    }));
  };

  // Variant handling
  const handleVariantChange = (index, key, value) => {
    const newVariants = [...variantState.variants];
    newVariants[index] = { ...newVariants[index], [key]: value };
    setVariantState(prev => ({ ...prev, variants: newVariants }));
  };

  const handleNewVariantChange = (placeholder, key, value) => {
    setVariantState(prev => ({
      ...prev,
      newVariant: { ...prev.newVariant, [key]: value }
    }));
  };

  const saveNewVariant = () => {
    const { newVariant } = variantState;
    
    if (!newVariant.variant_name || !newVariant.price || !newVariant.countInStock || !newVariant.weight) {
      toast.error("Lūdzu aizpildi visus laukus");
      return;
    }

    setVariantState(prev => ({
      ...prev,
      variants: [...prev.variants, newVariant],
      newVariant: {
        variant_name: "",
        variant_name_english: "",
        price: null,
        discount_price: null,
        countInStock: null,
        weight: null,
        height: null,
        width: null,
        length: null,
        sku: ""
      },
      addNewVariant: false,
    }));
  };

  const removeVariant = (index) => {
    setVariantState(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  // Image upload utilities
  const uploadImage = async (file, originalName) => {
    try {
      if (file == null) return;
  
      const imageName = v4();
      const maxWidthOrHeight = 800;
  
      const compressedFile = await imageCompression(file, {
        maxWidthOrHeight,
        useWebWorker: true,
      });
  
      const imageRef = ref(storage, `product_images/${imageName}`);
      const uploadTask = uploadBytesResumable(imageRef, compressedFile);
  
      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            // Progress function - you can add progress tracking here if needed
          },
          (error) => {
            toast.error("Something went wrong with image upload");
            reject(error);
          },
          () => {
            toast.success("Image uploaded successfully");
            resolve(imageName);
          }
        );
      });
    } catch (error) {
      toast.error("Something went wrong with image compression");
      throw error;
    }
  };

  const uploadGallery = async (files) => {
    const names = [];
    for (const file of files) {
      const name = await uploadImage(file, file.name);
      names.push(name);
    }
    return names;
  };

  const validateInputs = () => {
    if (!formData.title.trim()) {
      toast.error("Product title is required");
      return false;
    }
    if (!formData.category.trim()) {
      toast.error("Product category is required");
      return false;
    }
    if (!formData.short_description.trim()) {
      toast.error("Product short description is required");
      return false;
    }
    if (!formData.description.trim()) {
      toast.error("Product description is required");
      return false;
    }
    if (variantState.variants.length === 0) {
      toast.error("At least one product variant is required");
      return false;
    }

    const hasImage = imageState.image && (imageState.image.src || imageState.image.name);
    const hasExistingImage = productId && product?.image;
    
    if (!hasImage && !hasExistingImage) {
      toast.error("Main image is required");
      return false;
    }
    
    return true;
  }

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!validateInputs()) {
      setLoading(false);
      return;
    }
  
    try {
      let updatedProduct = { ...productObject };
  
      // Handle main image
      if (imageState.photoEditted && imageState.imageFile) {
        const imageName = await uploadImage(imageState.imageFile, imageState.image.name);
        updatedProduct.image = imageName;
      } else if (productId && product?.image?.name) {
        // Use existing image name for updates
        updatedProduct.image = product.image.name;
      } else if (productId && typeof product?.image === 'string') {
        // Handle case where image is already a string
        updatedProduct.image = product.image;
      } else if (updatedProduct.image?.name) {
        // Use current image name
        updatedProduct.image = updatedProduct.image.name;
      } else {
        // For new products without uploaded image, this should not happen due to validation
        delete updatedProduct.image;
      }
  
      // Handle gallery images
      if (imageState.galleryEditted) {
        const newGalleryImages = imageState.newImageFiles.length > 0 
          ? await uploadGallery(imageState.newImageFiles)
          : [];
        
        const existingGalleryImages = imageState.gallery
          ? imageState.gallery.filter((image) => !image.isNew).map((image) => image.name)
          : [];
        
        const allGalleryImages = [...existingGalleryImages, ...newGalleryImages];
        updatedProduct.images = allGalleryImages;
      } else if (productId && product?.images) {
        // Use existing gallery for updates
        updatedProduct.images = product.images.map((image) => image.name);
      } else if (updatedProduct.images) {
        // Use current gallery
        updatedProduct.images = updatedProduct.images.map((image) => 
          typeof image === 'string' ? image : image.name
        );
      } else {
        updatedProduct.images = [];
      }
  
      // Convert variant prices back to cents
      updatedProduct.variants = updatedProduct.variants.map((variant) => {
        const price = Number(variant.price) * 100;
        const discount_price = Number(variant.discount_price) * 100;
        return { ...variant, price, discount_price };
      });
  
      if (productId) {
        dispatch(editProduct({ ...updatedProduct, _id: productId }));
      } else {
        dispatch(addProduct(updatedProduct));
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error("Error saving product");
      setLoading(false);
    }
  };

  const handleProductRenew = () => {
    dispatch(renewProduct(productId));
    navigate("/edit-products");
  };

  if (isLoading || loading) return <Spinner />;

  return (
    <div className="add-product-page">
      <div className="add-product">
        <div className="add-product-form">
          <h2 className="light padding-20-bottom">
            {productId ? "Rediģēt produktu" : "Pievienot produktu"}
          </h2>
          {product && product.status === "inactive" && (
            <div className="flex padding-10-bottom">
              <ButtonPrimary
                text="Renew Product"
                onClick={handleProductRenew}
              />
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="product-input-container">
              <label>Nosaukums</label>
              <input
                className="title"
                type="text"
                placeholder="Nosaukums"
                value={formData.title}
                onChange={(e) => updateFormField('title', e.target.value)}
              />
              <input
                className="title"
                type="text"
                placeholder="Nosaukums angliski"
                value={formData.title_english}
                onChange={(e) => updateFormField('title_english', e.target.value)}
              />
            </div>
            <div className="product-input-container">
              <label>Latīniskais nosaukums</label>
              <input
                className="title"
                type="text"
                placeholder="Latīniskais nosaukums"
                value={formData.latin_title}
                onChange={(e) => updateFormField('latin_title', e.target.value)}
              />
            </div>
            <div className="product-input-container">
              <label>Kategorija</label>
              <input
                className="category"
                type="text"
                placeholder="Kategorija"
                value={formData.category}
                onChange={(e) => updateFormField('category', e.target.value)}
              />
              <input
                className="category"
                type="text"
                placeholder="Kategorija angliski"
                value={formData.category_english}
                onChange={(e) => updateFormField('category_english', e.target.value)}
              />
            </div>
            <div className="product-input-container">
              <label>Short Description</label>
              <textarea
                className="short-desc"
                type="text"
                placeholder="Īsais apraksts"
                value={formData.short_description}
                onChange={(e) => updateFormField('short_description', e.target.value)}
              />
              <textarea
                className="short-desc"
                type="text"
                placeholder="Īsais apraksts angliski"
                value={formData.short_description_english}
                onChange={(e) => updateFormField('short_description_english', e.target.value)}
              />
            </div>
            <div className="product-input-container">
              <label>Description</label>
              <ReactQuill 
                height={100} 
                placeholder="Apraksts" 
                className="my-quill" 
                value={formData.description} 
                onChange={(value) => updateFormField('description', value)} 
              />
              <ReactQuill 
                height={100} 
                placeholder="Apraksts angliski" 
                className="my-quill" 
                value={formData.description_english} 
                onChange={(value) => updateFormField('description_english', value)} 
              />
            </div>
            <div className="form-group double">
              <div className="product-input-container">
                <label>Augšanas ilgums (no)</label>
                <input
                  type="number"
                  value={formData.grow_time_min || ''}
                  placeholder="Augšanas ilgums (no)"
                  onChange={(e) => updateFormField('grow_time_min', Number(e.target.value))}
                />
              </div>
              <div className="product-input-container">
                <label>Augšanas ilgums (līdz)</label>
                <input
                  type="number"
                  placeholder="Augšanas ilgums (līdz)"
                  value={formData.grow_time_max || ''}
                  onChange={(e) => updateFormField('grow_time_max', Number(e.target.value))}
                />
              </div>
            </div>
            <div className="form-group double">
              <div className="product-input-container">
                <label>Augšanas temperatūra (no)</label>
                <input
                  type="number"
                  placeholder="Augšanas temperatūra (no)"
                  value={formData.grow_temp_min || ''}
                  onChange={(e) => updateFormField('grow_temp_min', Number(e.target.value))}
                />
              </div>
              <div className="product-input-container">
                <label>Augšanas temperatūra (līdz)</label>
                <input
                  type="number"
                  placeholder="Augšanas temperatūra (līdz)"
                  value={formData.grow_temp_max || ''}
                  onChange={(e) => updateFormField('grow_temp_max', Number(e.target.value))}
                />
              </div>
            </div>
            <div className="product-input-container">
              <label>Derīguma termiņš</label>
              <input
                type="number"
                placeholder="Derīguma termiņš"
                value={formData.valid_for || ''}
                onChange={(e) => updateFormField('valid_for', Number(e.target.value))}
              />
            </div>
            <div className="form-group double">
              <div className="product-input-container">
              <label>Variācijas tips:</label>
              <input
                type="text"
                placeholder="Kāda veida variācija (svars, izmērs, krāsa)"
                value={formData.variant_type}
                onChange={(e) => updateFormField('variant_type', e.target.value)}
              />
              <input
                type="text"
                placeholder="Kāda veida variācija (svars, izmērs, krāsa) angliski"
                value={formData.variant_type_english}
                onChange={(e) => updateFormField('variant_type_english', e.target.value)}
              />
              </div>
              <div className="product-input-container">
                <label>Produkta kārta sarakstā (1-99)</label>
                <input
                  type="number"
                  placeholder="1 rādīsies pirmais, 99 pēdējais"
                  value={formData.sort_order}
                  onChange={(e) => updateFormField('sort_order', Number(e.target.value))}
                />
              </div>
            </div>
            <input
              type="file"
              ref={singleFileInputRef}
              style={{ display: "none" }}
              onChange={handleSingleFileChange}
            />
            <input
              type="file"
              multiple
              ref={multipleFileInputRef}
              style={{ display: "none" }}
              onChange={handleMultipleFileChange}
            />

            <div className="variant-section padding-20-top">
              <h4 className="light">Produkta variācijas</h4>
              {variantState.variants.map((variant, index) => (
                <VariantForm 
                  key={index}
                  variant_type={formData.variant_type} 
                  handleSubmit={handleSubmit} 
                  variant={variant} 
                  handleVariantChange={handleVariantChange} 
                  saveVariant={() => {}} 
                  removeVariant={removeVariant} 
                  index={index} 
                />
              ))}
              
              {variantState.addNewVariant && (
                <VariantForm 
                  variant_type={formData.variant_type} 
                  handleSubmit={handleSubmit} 
                  variant={variantState.newVariant} 
                  handleVariantChange={handleNewVariantChange} 
                  saveVariant={saveNewVariant} 
                  removeVariant={() => {}} 
                  index={-1}
                />
              )}
            </div>
          </form>
          <div className="button-container padding-20-top">
            <ButtonPrimary 
              text={variantState.addNewVariant ? "Atcelt" : "Jauns izmērs"} 
              onClick={() => setVariantState(prev => ({ ...prev, addNewVariant: !prev.addNewVariant }))} 
            />
          </div>
        </div>
        
        <div className="add-product-card-container">
          <div className="add-product-card">
            <ProductCard product={productObject} admin={true}/>
          </div>

          <div className="added-image-gallery">
            {imageState.gallery.map((image, index) => (
              <div key={index} className="added-gallery-image">
                <img
                  src={image.src}
                  alt={"product.title"}
                  className={'product-image'}
                />
                <span
                    className="admin-remove-image"
                    onClick={() => handleImageDelete(index)}
                  >
                    <FaTimes />
                  </span>
              </div>
            ))}
          </div>

          <div className="flex padding-40-top">
            <div
              className="button-container padding-20-right"
              onClick={() => singleFileInputRef.current.click()}
            >
              <ButtonPrimary text="Galvenā bilde" />
            </div>
            <div className="button-container padding-20-right">
              <ButtonPrimary text="Galerija" onClick={() => multipleFileInputRef.current.click()}/>
            </div>
            <div className="button-container" onClick={handleSubmit}>
              <ButtonPrimary text="Saglabāt izmaiņas" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminAddProduct;